import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  TrendingUp,
  MapPin,
  Camera,
  Target,
  Users,
  Award,
  ChevronRight,
  Activity,
  Clock,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useProspects } from "../../context/ProspectsContext";
import { KpiCard } from "../../components/sales/KpiCard";
import { ProspectCard } from "../../components/sales/ProspectCard";
import { ModeToggle } from "../../components/ModeToggle";
import { api } from "../../lib/api";

const greeting = () => {
  const h = new Date().getHours();
  if (h < 11) return "Selamat pagi";
  if (h < 15) return "Selamat siang";
  if (h < 18) return "Selamat sore";
  return "Selamat malam";
};

const todayLabel = () => {
  return new Date().toLocaleDateString("id-ID", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
};

const jakartaDateKey = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Jakarta" }).format(date);
};

const QuickAction = ({ icon: Icon, label, to, testId, color = "red" }) => {
  const navigate = useNavigate();
  const colors = {
    red: "bg-honda-red-light text-honda-red",
    blue: "bg-blue-100 text-blue-600",
    green: "bg-green-100 text-green-600",
    orange: "bg-orange-100 text-orange-600",
  };
  return (
    <button
      onClick={() => navigate(to)}
      data-testid={testId}
      className="honda-card p-3 flex flex-col items-center justify-center gap-2 hover:border-honda-red transition-colors"
    >
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colors[color]}`}>
        <Icon size={20} strokeWidth={2.2} />
      </div>
      <span className="text-[11px] font-semibold text-honda-ink text-center leading-tight">
        {label}
      </span>
    </button>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const { prospects } = useProspects();
  const navigate = useNavigate();
  const [salesTarget, setSalesTarget] = useState(null);
  const [coverageStats, setCoverageStats] = useState({
    visited: 0,
    prospect: 0,
    closing: 0,
    total: 0,
    coverage_score: 0,
  });

  const loadTodayCoverage = useCallback(async () => {
    try {
      const { data } = await api.get("/coverage/stats", { params: { days: 366 } });
      console.log("[HSCIS][sales-dashboard] query dashboard sales result", {
        currentUser: user ? { id: user.id, role: user.role, dealer_id: user.dealer_id } : null,
        stats: data,
      });
      setCoverageStats(data);
    } catch (err) {
      console.log("[HSCIS][sales-dashboard] query dashboard sales result", {
        error: err?.response?.data?.detail || err?.message,
      });
    }
  }, [user]);

  useEffect(() => {
    loadTodayCoverage();
    window.addEventListener("hscis:coverage-checkin-saved", loadTodayCoverage);
    return () => window.removeEventListener("hscis:coverage-checkin-saved", loadTodayCoverage);
  }, [loadTodayCoverage]);

  useEffect(() => {
    const today = jakartaDateKey();
    const [year, month] = today.split("-");
    Promise.all([
      api.get("/targets/settings", { params: { scope_type: "sales", scope_id: user?.id, month: Number(month), year: Number(year) } }),
      api.get("/targets/sales", { params: { salesman_id: user?.id, month: Number(month), year: Number(year) } }),
    ])
      .then(([settingsRes, legacyRes]) => setSalesTarget(settingsRes.data?.[0] || legacyRes.data?.[0] || null))
      .catch(() => setSalesTarget(null));
  }, [user?.id]);

  // Backend sudah filter by role — prospects adalah milik user ini
  const myProspects = prospects;
  const recent = myProspects.slice(0, 3);

  // KPI dihitung dari data real
  const todayJakarta = useMemo(() => jakartaDateKey(), []);
  const prospectsToday = myProspects.filter((p) => jakartaDateKey(p.created_at) === todayJakarta).length;
  const closingMonth = myProspects.filter((p) => p.status === "closing").length;
  const coverageToday = coverageStats.total || 0;
  const babelCoverageScore = Math.min(Number(coverageStats.coverage_score || 0), 100);
  const babelCoverageLabel = `${coverageStats.area_valid || 0} dari ${coverageStats.target_area || 0} kabupaten/kota`;
  const targetVisits = salesTarget?.target_checkin ?? salesTarget?.target_visits ?? 0;
  const targetProspects = salesTarget?.target_prospect ?? salesTarget?.target_prospects ?? 0;
  const targetClosing = salesTarget?.target_closing || 0;

  return (
    <div data-testid="sales-dashboard" className="pb-4">
      {/* Header */}
      <div className="bg-gradient-to-br from-honda-red to-[#9F140E] text-white px-5 pt-12 pb-8 rounded-b-3xl shadow-lg">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-white/70 font-semibold">
              {todayLabel()}
            </p>
            <p className="mt-1 text-base text-white/90">
              {greeting()},
            </p>
            <h1 className="font-heading font-extrabold text-2xl leading-tight">
              {user?.name?.split(" ")[0] || "Sales"}!
            </h1>
            <p className="text-xs text-white/80 mt-1">
              {user?.dealer} · NIP {user?.nip}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ModeToggle />
            <button
              data-testid="dashboard-notification-button"
              className="w-10 h-10 rounded-full bg-white/15 backdrop-blur flex items-center justify-center hover:bg-white/25 transition-colors relative"
            >
              <Bell size={18} />
              <span className="absolute top-2 right-2 w-2 h-2 bg-yellow-300 rounded-full" />
            </button>
            {user?.avatar && (
              <img
                src={user.avatar}
                alt={user.name}
                className="w-10 h-10 rounded-full border-2 border-white/40 object-cover"
              />
            )}
          </div>
        </div>

        {/* Live status pill */}
        <div className="mt-5 inline-flex items-center gap-2 bg-white/15 backdrop-blur px-3 py-1.5 rounded-full">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400" />
          </span>
          <span className="text-[11px] font-semibold">GPS Aktif · Tracking 30s</span>
        </div>
      </div>

      {/* KPI grid */}
      <div className="px-5 -mt-5 grid grid-cols-2 gap-3">
        <KpiCard
          icon={Target}
          label="Kunjungan Hari Ini"
          value={coverageStats.visited || coverageToday}
          sub={targetVisits ? `Target ${targetVisits} · ${Math.round(((coverageStats.visited || coverageToday) / targetVisits) * 100)}%` : "Target belum diset"}
          accent="red"
          testId="kpi-prospect-today"
        />
        <KpiCard
          icon={Users}
          label="Prospek Hari Ini"
          value={coverageStats.prospect || prospectsToday}
          sub={targetProspects ? `Target ${targetProspects} · ${Math.round(((coverageStats.prospect || prospectsToday) / targetProspects) * 100)}%` : "Target belum diset"}
          accent="blue"
          testId="kpi-prospect-week"
        />
        <KpiCard
          icon={Award}
          label="Closing Hari Ini"
          value={coverageStats.closing || 0}
          sub={targetClosing ? `Target ${targetClosing} · ${Math.round(((coverageStats.closing || closingMonth) / targetClosing) * 100)}%` : `Bulan ini ${closingMonth}`}
          accent="green"
          testId="kpi-closing-month"
        />
        <KpiCard
          icon={Activity}
          label="Babel Coverage"
          value={`${babelCoverageScore}%`}
          sub={babelCoverageLabel}
          accent="orange"
          testId="kpi-coverage"
        />
      </div>

      {/* Today summary */}
      <div className="px-5 mt-5">
        <div className="mb-4 honda-card p-4">
          <p className="honda-label">Target Bulan Ini</p>
          <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
            <div><p className="text-honda-muted">Kunjungan</p><p className="mt-1 font-heading text-lg font-extrabold text-honda-ink">{coverageToday}/{targetVisits || "—"}</p></div>
            <div><p className="text-honda-muted">Prospek</p><p className="mt-1 font-heading text-lg font-extrabold text-honda-ink">{myProspects.length}/{targetProspects || "—"}</p></div>
            <div><p className="text-honda-muted">Closing</p><p className="mt-1 font-heading text-lg font-extrabold text-honda-ink">{closingMonth}/{targetClosing || "—"}</p></div>
          </div>
        </div>
        <div className="honda-card p-4 bg-gradient-to-br from-honda-ink to-[#2d2d2d] text-white border-honda-ink">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-white/60 font-semibold">
                Ringkasan Hari Ini
              </p>
              <p className="font-heading font-bold text-lg mt-1">Performa Anda</p>
            </div>
            <TrendingUp className="text-honda-red" size={28} />
          </div>
          <div className="grid grid-cols-3 gap-2 mt-4">
            <div>
              <p className="text-[10px] text-white/60 uppercase tracking-wider flex items-center gap-1">
                <MapPin size={10} /> Jarak
              </p>
              <p className="font-heading font-bold text-base mt-0.5">
                — km
              </p>
            </div>
            <div className="border-x border-white/15 px-2">
              <p className="text-[10px] text-white/60 uppercase tracking-wider flex items-center gap-1">
                <Clock size={10} /> Durasi
              </p>
              <p className="font-heading font-bold text-base mt-0.5">
                —
              </p>
            </div>
            <div>
              <p className="text-[10px] text-white/60 uppercase tracking-wider flex items-center gap-1">
                <Target size={10} /> Konversi
              </p>
              <p className="font-heading font-bold text-base mt-0.5">
                {myProspects.length > 0
                  ? `${Math.round((closingMonth / myProspects.length) * 100)}%`
                  : "0%"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="px-5 mt-6">
        <p className="honda-label">Aksi Cepat</p>
        <div className="grid grid-cols-4 gap-2 mt-2">
          <QuickAction
            icon={MapPin}
            label="Live Tracking"
            to="/sales/tracking"
            testId="qa-tracking"
            color="blue"
          />
          <QuickAction
            icon={Camera}
            label="Tambah Prospek"
            to="/sales/add"
            testId="qa-add"
            color="red"
          />
          <QuickAction
            icon={Users}
            label="Prospek"
            to="/sales/prospects"
            testId="qa-prospects"
            color="green"
          />
          <QuickAction
            icon={TrendingUp}
            label="Performa"
            to="/sales/performance"
            testId="qa-performance"
            color="orange"
          />
        </div>
      </div>

      {/* Recent prospects */}
      <div className="px-5 mt-6">
        <div className="flex items-center justify-between">
          <p className="honda-label">Prospek Terbaru</p>
          <button
            data-testid="see-all-prospects"
            onClick={() => navigate("/sales/prospects")}
            className="text-xs text-honda-red font-semibold flex items-center gap-0.5 hover:underline"
          >
            Lihat semua <ChevronRight size={14} />
          </button>
        </div>
        <div className="mt-3 space-y-2.5">
          {recent.length === 0 ? (
            <div className="honda-card p-6 text-center">
              <p className="text-sm text-honda-muted">Belum ada prospek hari ini.</p>
              <button
                onClick={() => navigate("/sales/add")}
                className="mt-3 honda-btn-primary text-sm"
                data-testid="empty-add-prospect"
              >
                + Tambah Prospek Pertama
              </button>
            </div>
          ) : (
            recent.map((p) => <ProspectCard key={p.id} prospect={p} />)
          )}
        </div>
      </div>
    </div>
  );
}
